import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { ChecklistEntrega } from "../../domain/models/checklist_entrega";
import { ChecklistEntregaLista } from "../../domain/models/checklist_entrega_lista";
import { useChecklistEntregaListaFilters } from "../hooks/checklist_entrega_lista/filters";
import { useChecklistEntregaListaPagination } from "../hooks/checklist_entrega_lista/pagination";
import { ChecklistEntregaCopiaForm } from "../schemas/checklist_entrega_lista/checklistEntregaCopia";
import { ChecklistEntregaCadastroForm } from "../schemas/checklist_entrega_lista/checklistEntregaCadastro";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

type LoadChecklistEntregaListaState = {
    status?: number,
    codigo?: string,
    descricao?: string,

    currentPage?: number;
    rowsPerPage?: number;
}

export const useChecklistEntregaListaViewModel = () => {

    const {
        entregaObras: {
            getAllChecklistEntregaUseCase,
            getChecklistEntregaByIdUseCase,
            updateChecklistEntregaUseCase,
            createChecklistEntregaUseCase,
            copyChecklistEntregaUseCase,
            deleteChecklistEntregaUseCase,
        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        },
        reports: {
            getReportChecklistEntregaListaUseCase,
        }

    } = useDependencyInjection();

    const [reportViewerState, startReportViewerProcess] = useReportViewer();

    const backOrNavigate = useBackOrNavigate();

    const { pushErrorNotification, pushInfoNotification, setPageLoading, setCustomLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openCopiaModal, setOpenCopiaModal] = useState(false);
    const [openCadastroModal, setOpenCadastroModal] = useState(false);

    const [selectedIdChecklistEntrega, setSelectedIdChecklistEntrega] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [checklistEntregaLista, setChecklistEntregaLista] = useState<ChecklistEntregaLista[]>();
    const [selectedChecklistEntrega, setSelectedChecklistEntrega] = useState<ChecklistEntrega | undefined>();

    const filters = useChecklistEntregaListaFilters();
    const pagination = useChecklistEntregaListaPagination();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })


    //#region CARREGAMENTO.

    const loadChecklistEntregaLista = useCallback(async (funcState?: LoadChecklistEntregaListaState) => {
        try {
            setLoading(true);
            const filteredStatus = funcState?.status ?? filters.status;
            const filteredCodigo = funcState?.codigo ?? filters.codigo;
            const filteredDescricao = funcState?.descricao ?? filters.descricao;

            const filteredPage = funcState?.currentPage ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllChecklistEntregaUseCase.execute(
                filteredCodigo,
                filteredDescricao,
                filteredStatus,

                filteredPage,
                filteredRowsPerPage,
            );

            setChecklistEntregaLista(getAllChecklistEntregaUseCase.items);

            pagination.setRowsPerPage(getAllChecklistEntregaUseCase.pageSize);
            pagination.setTotalItems(getAllChecklistEntregaUseCase.totalItems);
        } catch (e: unknown) {
            setChecklistEntregaLista([]);
            pushErrorNotification("Error", processError(e, "Falha ao buscar lista de checklist de entrega!"));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        getAllChecklistEntregaUseCase,
        pushErrorNotification
    ]);


    const loadChecklistEntregaById = useCallback(async (idChecklist: number) => {
        try {
            setLoading(true);
            const checklist = await getChecklistEntregaByIdUseCase.execute(idChecklist);
            setSelectedChecklistEntrega(checklist);

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [
        getChecklistEntregaByIdUseCase,
        pushErrorNotification]);

    //#endregion




    //#region PAGE START
    usePageStartBootstrap(async () => {
        setPageLoading(true);
        try {
            if (checklistEntregaLista === undefined) {
                await loadChecklistEntregaLista();
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de checklist de entrega!")[0]);
        } finally {
            setPageLoading(false);
        }
    });


    //#endregion




    //#region NOVO/EDITAR.

    const handleChecklistEntregaOpenState = useCallback(async (idChecklistEntrega: number | null, openState: boolean) => {
        setOpenCadastroModal(openState);
        try {
            if (openState) {
                if (idChecklistEntrega) {
                    await loadChecklistEntregaById(idChecklistEntrega);
                } else {
                    setSelectedChecklistEntrega(undefined);
                    setSelectedIdChecklistEntrega(undefined);
                }
            } else {
                setSelectedChecklistEntrega(undefined);
                setSelectedIdChecklistEntrega(undefined);
            }

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao iniciar criação de novo checklist de entrega!'));
        }
    }, [
        loadChecklistEntregaById,
        setOpenCadastroModal,
        pushErrorNotification
    ]);


    const handleSubmitChecklistEntrega = useCallback(async (form: ChecklistEntregaCadastroForm) => {
        try {
            setSaving(true);
            if (form.idChecklistEntrega) {
                await updateChecklistEntregaUseCase.execute(
                    form.idChecklistEntrega,
                    form.codigo,
                    form.descricao,
                    form.revisao ?? undefined,
                    form.campo1 ?? undefined,
                    form.campo2 ?? undefined,
                    form.campo3 ?? undefined,
                    form.campo4 ?? undefined,
                    form.ativo === 1 ? true : false,
                );
                pushInfoNotification("Sucesso", " Checklist de entrega atualizado com sucesso!", { isAutoFinish: true });
            } else {
                await createChecklistEntregaUseCase.execute(
                    form.codigo,
                    form.descricao,
                    form.revisao ?? undefined,
                    form.campo1 ?? undefined,
                    form.campo2 ?? undefined,
                    form.campo3 ?? undefined,
                    form.campo4 ?? undefined,
                );
                pushInfoNotification("Sucesso", " Checklist de entrega criado com sucesso!", { isAutoFinish: true });
            }
            await loadChecklistEntregaLista();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar Checklist de entrega!"));
        } finally {
            setSaving(false);
            setOpenCadastroModal(false);
            setSelectedChecklistEntrega(undefined)
        }
    }, [
        createChecklistEntregaUseCase,
        updateChecklistEntregaUseCase,
        loadChecklistEntregaLista,
        pushErrorNotification,
        pushInfoNotification]);

    //#endregion





    //#region PAGINAÇÃO.

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadChecklistEntregaLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [
        loadChecklistEntregaLista,
        pushErrorNotification
    ]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadChecklistEntregaLista({ currentPage: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadChecklistEntregaLista, pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadChecklistEntregaLista({ rowsPerPage, currentPage: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadChecklistEntregaLista, pushErrorNotification]);

    //#endregion



    //#region COPIA

    const handleChecklistEntregaCopiaOpenState = useCallback((idChecklistEntrega: number | null, openState: boolean) => {
        if (openState) {
            if (idChecklistEntrega) {
                setSelectedIdChecklistEntrega(idChecklistEntrega);
                const checklistEntrega = checklistEntregaLista?.find((item) => item.idChecklistEntrega === idChecklistEntrega);
                setSelectedChecklistEntrega(checklistEntrega);
                setOpenCopiaModal(true);
            } else {
                setSelectedIdChecklistEntrega(undefined);
                setSelectedChecklistEntrega(undefined);
                setOpenCopiaModal(false);
            }
        }
        setOpenCopiaModal(openState);
    }, [
        checklistEntregaLista,
    ]);


    const handleSubmitChecklistEntregaCopia = useCallback(async (form: ChecklistEntregaCopiaForm) => {
        try {
            setSaving(true);
            if (selectedIdChecklistEntrega) {
                await copyChecklistEntregaUseCase.execute(
                    selectedIdChecklistEntrega,
                    form.codigo ?? undefined,
                    form.descricao ?? undefined,
                    form.revisao ?? undefined);
                pushInfoNotification("Sucesso", " Checklist de entrega copiado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel copiar Checklist de entrega!", { isAutoFinish: true });
            }
            await loadChecklistEntregaLista();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Copiar Checklist de entrega!"));
        } finally {
            setSaving(false);
            setOpenCopiaModal(false);
            setSelectedIdChecklistEntrega(undefined)
        }
    }, [
        selectedIdChecklistEntrega,
        copyChecklistEntregaUseCase,
        loadChecklistEntregaLista,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion






    //#region DELECAO 

    const handleChecklistEntregaDeleteOpenState = useCallback((idChecklistEntrega: number | null, openState: boolean) => {
        if (openState) {
            if (idChecklistEntrega) {
                setSelectedIdChecklistEntrega(idChecklistEntrega);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdChecklistEntrega(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, []);


    const handleConfirmarDeletarChecklistEntrega = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdChecklistEntrega) {
                await deleteChecklistEntregaUseCase.execute(selectedIdChecklistEntrega);
                pushInfoNotification("Sucesso", " Checklist de entrega deletado com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Checklist de entrega!", { isAutoFinish: true });
            }
            await loadChecklistEntregaLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Checklist de entrega!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdChecklistEntrega(undefined)
        }
    }, [
        selectedIdChecklistEntrega,
        deleteChecklistEntregaUseCase,
        loadChecklistEntregaLista,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion





    //#region PESQUISA.

    const handleCodigoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setCodigo(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadChecklistEntregaLista({ codigo: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar clientes!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadChecklistEntregaLista, pushErrorNotification]
    );


    const handleDescricaoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            filters.setDescricao(newValue ?? undefined);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadChecklistEntregaLista({ descricao: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar clientes!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadChecklistEntregaLista, pushErrorNotification]
    );

    const handleStatusChange = useDebouncedCallback(
        debouncedRef,
        (newValue: number | null) => {

            filters.setStatus(newValue ?? 2);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadChecklistEntregaLista({ status: newValue ?? 2, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar checklist de entrega!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadChecklistEntregaLista, pushErrorNotification]);

    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadChecklistEntregaLista({
                codigo: '',
                descricao: '',
                status: 2,
                currentPage: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        loadChecklistEntregaLista,
        pushErrorNotification]);

    //#endregion 



    //#region CRIAÇÃO/EDIÇÃO ITENS

    const handleEditItens = useCallback(
        (id: number | null, openState: boolean) => {
            if (!openState) window.open(`/ChecklistEntregaAmbiente_lista?id=${id}`, "_blank", "noopener,noreferrer");
            else backOrNavigate(`/ChecklistEntregaAmbiente_lista?id=${id}`);
        },
        [backOrNavigate],
    );
    //#endregion



    //#region EXPORTAÇÃO DE RELATÓRIO

    const handleExportReport = useCallback(async (idChecklistEntrega: number) => {
        try {
            setCustomLoading(true, "Gerando relatório...");
            await startReportViewerProcess({
                fetchCallback: async (requiredMimeType) =>
                    await getReportChecklistEntregaListaUseCase.getReportChecklistEntregaLista(requiredMimeType, idChecklistEntrega)
            });

        } catch (e: unknown) {
            pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatorio!'));
        } finally {
            setCustomLoading(false);
        }
    }, [
        getReportChecklistEntregaListaUseCase,
        startReportViewerProcess,
        setCustomLoading,
        pushErrorNotification,
    ]);



    //#endregion


    //#region History

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedChecklistEntrega?.idChecklistEntrega ?? 0, LogTipoTabela.CHECKLIST_ENTREGA, true);
            }
        }
    }, [
        selectedChecklistEntrega,
        changeHistory,
    ]);

    //#endregion


    return {
        loading,
        saving,
        checklistEntregaLista: checklistEntregaLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        selectedIdChecklistEntrega,
        openCopiaModal,
        openCadastroModal,
        selectedChecklistEntrega,
        changeHistory,
        reportViewerState,

        handleExportReport,
        handleEditItens,
        handleOpenChangeHistory,
        handleSubmitChecklistEntrega,
        handleChecklistEntregaOpenState,
        handleChecklistEntregaCopiaOpenState,
        handleSubmitChecklistEntregaCopia,
        handleChecklistEntregaDeleteOpenState,
        handleConfirmarDeletarChecklistEntrega,
        handleCodigoChange,
        handleDescricaoChange,
        handleStatusChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,
    };
}


